import React, { useCallback, useEffect, useState } from "react";
import { onAuthStateChanged, User, signOut } from "firebase/auth";
import { auth } from "./firebase";
import { Header } from "./components/Header";
// TODO(redesign-sprint-2): EventList/EventDetail/Filters still use legacy light theme
import { EventList } from "./components/EventList";
import { EventDetailPanel } from "./components/EventDetail";
import { LoginPage } from "./components/LoginPage";
import { TerritorySelector } from "./components/TerritorySelector";
import { EventFilters, FilterState } from "./components/EventFilters";
import { fetchEvents, addManualEvent, fetchTeamAttendance, getStrategy, PhoenixContext } from "./api";
import { EventsResponse, GeoLevel, TeamAttendanceMap } from "./types";
import { AddEventDialog } from "./components/AddEventDialog";
import { StrategyUploadDialog } from "./components/StrategyUploadDialog";

interface TerritoryValue {
  level: GeoLevel;
  code: string;
}

const DEFAULT_TERRITORY: TerritoryValue = { level: "senat", code: "51" };

const DEFAULT_FILTER: FilterState = {
  categories: [],
  minScore: 1,
  maxScore: 10,
  hideDefaults: true,
};

// SVG arrow used in empty-state (no emoji)
const ArrowLeftIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: "inline-block" }}
  >
    <path
      d="M19 12H5M5 12l6-6M5 12l6 6"
      stroke="rgba(255,255,255,0.2)"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [data, setData] = useState<EventsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<"date" | "priority">("date");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [territory, setTerritory] = useState<TerritoryValue>(DEFAULT_TERRITORY);
  const [filter, setFilter] = useState<FilterState>(DEFAULT_FILTER);
  const [teamAttendance, setTeamAttendance] = useState<TeamAttendanceMap>({});
  const [strategyContext, setStrategyContext] = useState<PhoenixContext | null>(null);
  const [strategyDialogOpen, setStrategyDialogOpen] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState<boolean>(() => {
    return localStorage.getItem("phoenix_prompt_dismissed") === "1";
  });

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthLoading(false);
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!user) {
      setStrategyContext(null);
      return;
    }
    getStrategy()
      .then((ctx) => setStrategyContext(ctx))
      .catch(() => setStrategyContext(null));
  }, [user]);

  const loadEvents = useCallback(
    (ter: TerritoryValue, fil: FilterState) => {
      if (!user) return;
      setLoading(true);
      fetchEvents({
        level: ter.level,
        code: ter.code || undefined,
        categories: fil.categories,
        minScore: fil.minScore,
        maxScore: fil.maxScore,
        hideDefaults: fil.hideDefaults,
      })
        .then((resp) => {
          setData(resp);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    },
    [user]
  );

  useEffect(() => {
    if (!user) return;
    loadEvents(territory, filter);
  }, [user, territory, filter, loadEvents]);

  useEffect(() => {
    if (!data || territory.level !== "senat") {
      setTeamAttendance({});
      return;
    }
    fetchTeamAttendance(territory.code)
      .then(setTeamAttendance)
      .catch(() => setTeamAttendance({}));
  }, [data, territory]);

  const handleTerritoryChange = (next: TerritoryValue) => {
    setTerritory(next);
    setSelectedId(null);
  };

  const handleFilterChange = (next: FilterState) => {
    setFilter(next);
    setSelectedId(null);
  };

  const handleCreditsChange = () => {
    loadEvents(territory, filter);
  };

  // Auth loading — thin white ring on dark bg
  if (authLoading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#050505",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div className="dwx-spinner" />
      </div>
    );
  }

  if (!user) {
    return <LoginPage onLogin={() => {}} />;
  }

  // Data loading — full-screen dark
  if (loading && !data) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#050505",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div className="dwx-spinner" style={{ margin: "0 auto" }} />
          <p
            style={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 300,
              fontSize: "0.8125rem",
              color: "#666666",
              marginTop: "1rem",
              letterSpacing: "0.04em",
            }}
          >
            Načítám akce...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (!data) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#050505",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <p
          style={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 300,
            fontSize: "0.875rem",
            color: "#f87171",
          }}
        >
          Nepodařilo se načíst data. Je API server spuštěný?
        </p>
      </div>
    );
  }

  const filtered = data.events
    .filter((e) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        e.title.toLowerCase().includes(q) ||
        e.city.toLowerCase().includes(q) ||
        (e.organizer || "").toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      if (sortBy === "priority") {
        return (b.priority_score ?? 0) - (a.priority_score ?? 0);
      }
      const da = a.date ?? "9999";
      const db = b.date ?? "9999";
      return da.localeCompare(db);
    });

  return (
    <div style={{ minHeight: "100vh", background: "#050505" }}>
      <Header
        eventCount={data.total}
        creditsRemaining={data.credits_remaining}
        creditsUsed={data.credits_used}
        userEmail={user?.email ?? undefined}
        onLogout={() => signOut(auth)}
      />

      {strategyContext === null
        && !bannerDismissed
        && territory.level === "senat"
        && (
          <div
            role="region"
            aria-label="Phoenix Electoral upload prompt"
            style={{
              margin: "12px 16px",
              padding: "14px 20px",
              borderRadius: 16,
              border: "1px solid rgba(255,255,255,0.10)",
              background: "rgba(20,20,28,0.85)",
              color: "#e6e6e6",
              display: "flex",
              alignItems: "center",
              gap: 16,
              backdropFilter: "blur(8px)",
              fontSize: 14,
            }}
          >
            <span style={{ flex: 1 }}>
              Pro personalizované briefy nahrajte výstupy{" "}
              <strong>Phoenix Electoral Analytics</strong>.
            </span>
            <button
              onClick={() => setStrategyDialogOpen(true)}
              style={{
                padding: "8px 16px",
                borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.20)",
                background: "rgba(255,255,255,0.06)",
                color: "#fff",
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              Nahrát Phoenix analýzy
            </button>
            <button
              onClick={() => {
                localStorage.setItem("phoenix_prompt_dismissed", "1");
                setBannerDismissed(true);
              }}
              aria-label="Zavřít"
              style={{
                padding: 4,
                borderRadius: 4,
                border: "none",
                background: "transparent",
                color: "rgba(255,255,255,0.50)",
                cursor: "pointer",
                fontSize: 18,
                lineHeight: 1,
              }}
            >×</button>
          </div>
        )
      }

      {/* Offset for fixed header */}
      <main
        className="max-w-7xl mx-auto px-4 lg:px-8 py-6"
        style={{ paddingTop: "calc(3.5rem + 1.5rem)", position: "relative" }}
      >
        {/* Subtle scanlines over main content */}
        <div className="main-scanlines" aria-hidden="true" />

        {/* Content sits above scanlines */}
        <div style={{ position: "relative", zIndex: 1 }}>
          {/* Territory selector */}
          <div className="mb-4">
            <TerritorySelector value={territory} onChange={handleTerritoryChange} />
          </div>

          {/* Category + score filters */}
          <div className="mb-4">
            <EventFilters value={filter} onChange={handleFilterChange} />
          </div>

          {/* Search + sort + add button */}
          <div
            className="flex flex-col sm:flex-row gap-3 mb-4"
          >
            <button
              onClick={() => setShowAddDialog(true)}
              className="btn-primary-hero"
              style={{ alignSelf: "flex-start" }}
            >
              + Přidat akci
            </button>

            <input
              type="text"
              placeholder="Hledat akci nebo město..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="dwx-input"
              style={{ flex: 1 }}
            />

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as "date" | "priority")}
              className="dwx-select"
              style={{ minWidth: "11rem" }}
            >
              <option value="date">Podle data</option>
              <option value="priority">Podle priority</option>
            </select>
          </div>

          {/* Stats bar */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "0.5rem 1rem",
              marginBottom: "1rem",
            }}
          >
            <span className="eyebrow-label" style={{ margin: 0 }}>
              {loading ? "Načítám..." : `${filtered.length} akcí`}
            </span>

            <span
              className="hidden sm:block"
              style={{
                width: "1px",
                height: "0.75rem",
                background: "rgba(255,255,255,0.1)",
              }}
            />

            {/* Priority legend dots */}
            <span className="eyebrow-label" style={{ margin: 0, display: "flex", alignItems: "center", gap: "0.375rem" }}>
              <span
                style={{
                  width: "0.5rem",
                  height: "0.5rem",
                  borderRadius: "50%",
                  background: "#4ade80",
                  boxShadow: "0 0 6px rgba(74, 222, 128, 0.6)",
                  display: "inline-block",
                  animation: "pulse-dot 2s ease-in-out infinite",
                }}
              />
              7-10
            </span>

            <span className="eyebrow-label" style={{ margin: 0, display: "flex", alignItems: "center", gap: "0.375rem" }}>
              <span
                style={{
                  width: "0.5rem",
                  height: "0.5rem",
                  borderRadius: "50%",
                  background: "#facc15",
                  boxShadow: "0 0 6px rgba(250, 204, 21, 0.5)",
                  display: "inline-block",
                }}
              />
              4-6
            </span>

            <span className="eyebrow-label" style={{ margin: 0, display: "flex", alignItems: "center", gap: "0.375rem" }}>
              <span
                style={{
                  width: "0.5rem",
                  height: "0.5rem",
                  borderRadius: "50%",
                  background: "#3f3f3f",
                  display: "inline-block",
                }}
              />
              1-3
            </span>
          </div>

          {/* Mobile: show detail OR list. Desktop: side by side */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-5 lg:gap-6">
            <div
              className={`lg:col-span-2 lg:max-h-[calc(100vh-220px)] lg:overflow-y-auto lg:pr-1 ${
                selectedId ? "hidden lg:block" : ""
              }`}
            >
              <EventList
                events={filtered}
                onSelect={setSelectedId}
                selectedId={selectedId}
                teamAttendance={teamAttendance}
              />
            </div>

            <div className={`lg:col-span-3 ${!selectedId ? "hidden lg:block" : ""}`}>
              {selectedId ? (
                <>
                  {/* Mobile back button — glass style */}
                  <button
                    onClick={() => setSelectedId(null)}
                    className="btn-secondary-hero lg:hidden"
                    style={{ marginBottom: "0.75rem", gap: "0.5rem" }}
                  >
                    <ArrowLeftIcon />
                    Zpět na seznam
                  </button>

                  <EventDetailPanel
                    eventId={selectedId}
                    onCreditsChange={handleCreditsChange}
                    obvodCode={territory.level === "senat" ? territory.code : undefined}
                  />
                </>
              ) : (
                /* Empty state — glass card, no emoji */
                <div
                  className="service-card animate-fade-in animate-delay-1"
                  style={{
                    borderRadius: "16px",
                    padding: "3.5rem 2rem",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      width: "2.5rem",
                      height: "2.5rem",
                      borderRadius: "50%",
                      background: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 1.25rem",
                    }}
                  >
                    <ArrowLeftIcon />
                  </div>

                  <h3
                    style={{
                      fontFamily: "'Syne', sans-serif",
                      fontWeight: 700,
                      fontSize: "1.0625rem",
                      letterSpacing: "-0.03em",
                      color: "#fafafa",
                      margin: "0 0 0.5rem",
                    }}
                  >
                    Vyberte akci ze seznamu
                  </h3>

                  <p
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontWeight: 300,
                      fontSize: "0.8125rem",
                      color: "#666666",
                      margin: 0,
                      letterSpacing: "0.01em",
                    }}
                  >
                    Klikněte na akci pro zobrazení briefu a návrhů příspěvků
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {showAddDialog && (
        <AddEventDialog
          onClose={() => setShowAddDialog(false)}
          onAdd={async (evt) => {
            await addManualEvent(evt);
            setShowAddDialog(false);
            loadEvents(territory, filter);
          }}
        />
      )}

      <StrategyUploadDialog
        open={strategyDialogOpen}
        onClose={() => setStrategyDialogOpen(false)}
        existingContext={strategyContext}
        onUploaded={() => {
          getStrategy().then(setStrategyContext).catch(() => {});
        }}
        onDeleted={() => {
          setStrategyContext(null);
          setBannerDismissed(false);
          localStorage.removeItem("phoenix_prompt_dismissed");
        }}
      />
    </div>
  );
}

export default App;
